<template>
  <div>
    <div id="preview">
      <section data-type="cover">
        <div class="cover__content">
          <div class="cover__bg-container">
            <img 
              src="https://camas.app/resources/static/images/images/image__1c1d9a37113ea9f56ac8ad071994a559.png"
              alt="cover background image"/>
          </div>
        </div>
        <div class="cover__banner">
          <div class="cover__banner-inner">
            <img 
              class="cover__logo"
              src="https://camas.app/resources/static/images/images/image__40f94364c998232c96ff71ee3fb00978.png"
              alt="Mitrex Logo"/>
            <div class="cover__summary-font">
              YOUR PROJECT SUMMARY
            </div>
            <div class="cover__banner-tagline">
              The Largest BIPV Manufacturer In The World
            </div>
          </div>
        </div>
      </section>

      <section data-type="mission" class="mission break-after">
        <div class="mission__bg-container">
          <img 
            src="https://camas.app/resources/static/images/images/image__a99d246c1593f6ba1f0aea03b183eab4.png"
            alt="mitrex mission background image">
        </div>
        <div class="mission__wrapper">
          <div class="mission__title">
            <img 
              class="target-icon"
              src="https://camas.app/resources/static/images/images/image__cfa941a8a3fee1fa6ca417439be1ec21.png"
              alt="target icon"/>
            <div class="title-line">Mitrex Mission:</div>
          </div>
          <div>
            Our mission is to be the catalyst that
            accelerates the adoption of sustainable,
            energy-generating, human-made
            structures
          </div>
        </div>
      </section>

      <section class="project-details break-after">
        <div class="project-details__left">
          <div class="orange-panel">
            <div class="panel__title">
              Project Details
            </div>
            <img 
              class="building-image" 
              alt="building image"
              :src="projectBuilding.image"/>

            <div>
              <div class="summary__section">
                <div class="panel__subtitle">
                  Type of Building
                </div>
                <div>
                  {{ projectBuilding.label }}
                </div>
              </div>
              
              <div class="summary__section">
                <div class="panel__subtitle">Location</div>
                <div>
                  {{ sunlightHours.location }}
                </div>
              </div>

              <div class="summary__section">
                <div class="panel__subtitle">
                  Products and Project Size
                </div>
                <div>
                  <template 
                    v-for="(measure, productType) in { cladding: 'SQFT', railing: 'LF', window: 'SQFT' }">
                    <div 
                      v-if="projectSummary[productType]"
                      :key="productType">
                      Solar {{ productType | titlecase }} - 
                      {{ projectSize[productType] | commasep(0) }} {{ measure }}
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </div>

          <div class="bottom-panel">
            <div class="panel__subtitle">
              Product Swatch Selected
            </div>
            <div class="product" v-if="projectSummary.cladding">
              <img
                class="product__image--cladding" 
                :src="projectCladding.image" 
                :alt="projectCladding.sample_name"/>
              <div>{{ projectCladding.sample_name }} Solar Cladding</div>
            </div>
            <div class="product" v-if="projectSummary.railing">
              <img 
                class="product__image" 
                :src="projectRailing.image" 
                :alt="projectRailing.sample_name"/>
              <div>{{ projectRailing.sample_name }} Solar Railing</div>
            </div>
            <div class="product" v-if="projectSummary.window">
              <img
                class="product__image" 
                :src="projectWindow.image" 
                :alt="projectWindow.sample_name"/>
              <div>{{ projectWindow.sample_name }} Solar Glass</div>
            </div>
          </div>
        </div>
        <div class="project-details__right">
          <div class="section__subsection">
            <div>Mitrex Calculator</div>
            <div class="section__title">
              Estimate Price for a {{ projectBuilding.label }} Building
            </div>
            <div class="section__desc">
              Please find below the project summary.
            </div>
          </div>
          <div class="section__subsection">
            <div class="section__title">
              Cost and Energy Results
            </div>
            <table class="table">
              <thead>
                <tr>
                  <th></th>
                  <th>Mitrex Power Agreement (MPA)</th>
                  <th>Direct Purchase Agreement (DPA)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>System Size (kW)</td>
                  <td>{{ Object.values(projectSummary)
                        .map(d => d.system_size)
                        .reduce((a,b) => a + b, 0) | commasep(0) }}</td>
                  <td>{{ Object.values(projectSummary)
                        .map(d => d.system_size)
                        .reduce((a,b) => a + b, 0) | commasep(0) }}</td>
                </tr>
                <tr>
                  <td>Total Cost ($)</td>
                  <td>{{ Object.values(projectSummary)
                        .map(d => d.total_cost_mpa)
                        .reduce((a,b) => a + b, 0) | commasep(0) }}</td>
                  <td>{{ Object.values(projectSummary)
                        .map(d => d.total_cost_dpa)
                        .reduce((a,b) => a + b, 0) | commasep(0) }}</td>
                </tr>
                <tr>
                  <td>30-Year Energy Revenue ($)</td>
                  <td>{{ Object.values(projectSummary)
                        .map(d => d.energy_revenue_mpa)
                        .reduce((a,b) => a + b, 0) * 30 | commasep(0) }}</td>
                  <td>{{ Object.values(projectSummary)
                        .map(d => d.energy_revenue_dpa)
                        .reduce((a,b) => a + b, 0) * 30 | commasep(0)}}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="section__subsection env-impact">
            <div class="section__title">
              Environmental Impact
            </div>
            <div class="section__desc">
              Environmental impact over 30 years
            </div>
            <div class="env-impact__graphics">
              <div class="env-impact__wrapper top">
                <div class="env-impact__item">
                  Reducing 
                  <div class="env-impact__number">
                    {{ Object.values(projectSummary)
                        .map(d => d.metric_tonnes_co2_per_year)
                        .reduce((a,b) => a + b, 0) * 30 | commasep(0) }} tonnes
                  </div>
                  of CO2
                </div>
                <div class="env-impact__item">
                  Planting 
                  <div class="env-impact__number">
                    {{ Object.values(projectSummary)
                      .map(d => d.trees_planted_per_year)
                      .reduce((a,b) => a + b, 0) * 30 | commasep(0) }}
                  </div>
                  Trees
                </div>
              </div>
              <img 
                src="https://camas.app/resources/static/images/images/image__e6d39c514ee953501fa4fe102daebbb9.jpg"
                alt="environmental impact"/>
              <div class="env-impact__wrapper bottom">
                <div class="env-impact__item extra-margin">
                  <div class="env-impact__number">
                    {{ Object.values(projectSummary)
                      .map(d => d.energy_per_year)
                      .reduce((a,b) => a + b, 0) * 30 | commasep(0) }} kWh
                  </div>
                  Green Energy Produced
                </div>
                <div class="env-impact__item">
                  Powering 
                  <div class="env-impact__number">
                    {{ Object.values(projectSummary)
                      .map(d => d.electricity_homes_per_year)
                      .reduce((a,b) => a + b, 0) * 30 | commasep(0) }}
                  </div>
                  Homes
                </div>
                <div class="env-impact__item">
                  Removing 
                  <div class="env-impact__number">
                    {{ Object.values(projectSummary)
                      .map(d => d.cars_off_road_per_year)
                      .reduce((a,b) => a + b, 0) * 30 | commasep(0) }}
                  </div>
                  Cars
                </div>
              </div>
              <div class="env-impact__footnote">
                *Source: Greenhouse Gas Equivalencies Calculator, US EPA
              </div>
            </div>
          </div>
          <div class="section__subsection" v-if="taxIncentives.length > 0">
            <div class="section__title">
              Tax Incentives
            </div>
            <div 
              class="tax-incentives" 
              v-for="(taxIncentive, i) in taxIncentives" :key="i">
              <a :href="taxIncentive.link" target="_blank">
                {{ taxIncentive.name }}
              </a>
            </div>
          </div>
        </div>
        <div class="running-footer">
          <div class="running-footer__left">
          </div>
          <div class="running-footer__right">
          </div>
          <div class="contact-info">
            <div class="contact-info__item">
              <img
                class="contact-info__icon" 
                src="https://camas.app/resources/static/images/images/image__24f0455ad0662fff46d21a7b13674f59.png"
                alt="phone icon"/>
              <div 
                class="contact-info__detail">
                +1-416-497-7120
              </div>
            </div>
            <div class="contact-info__item">
              <img
                class="contact-info__icon" 
                src="https://camas.app/resources/static/images/images/image__45cfdb856e64e025f18ff05ce3cf3f36.png"
                alt="email icon"/>
              <a 
                href="mailto:info@mitrex.com"
                class="contact-info__detail">
                info@mitrex.com
              </a>
            </div>
            <div class="contact-info__item">
              <img
                class="contact-info__icon" 
                src="https://camas.app/resources/static/images/images/image__b03f9e98af487c26df6b0c9b0f54dc44.png"
                alt="address icon"/>
              <div 
                href="www.mitrex.com"
                target="_blank"
                class="contact-info__detail">
                41 Racine Rd., Toronto, ON M9W 2Z4, Canada
              </div>
            </div>
            <div class="contact-info__item">
              <img
                class="contact-info__icon" 
                src="https://camas.app/resources/static/images/images/image__a2a0645456c08a74e0cdb1292d1ab5cf.png"
                alt="website icon"/>
              <a 
                href="www.mitrex.com"
                target="_blank"
                class="contact-info__detail">
                mitrex.com
              </a>
            </div>
          </div>
        </div>
      </section>

      <section class="solar-tech break-after">
        <div class="solar-cladding__left">
          <div class="orange-panel">
            <div class="solar-tech__title-wrapper">
              <img
                class="solar-tech__icon"
                alt="solar cladding icon" 
                src="https://camas.app/resources/static/images/images/image__ec5f418a12065c51e6d57c3d000a95c8.png"/>
              <div>Solar Technology</div>
            </div>
            <div class="solar-tech__tech-label">
              Monocrystalline Solar Cell
            </div>
            <img
              class="solar-tech__image--square"
              alt="solar cell image"  
              src="https://camas.app/resources/static/images/images/image__443c621c20b54bbb2561a1e5976739ac.png"/>
            <div>
              <p>
                This type of solar technology is the highest performing,
                most scalable option on the market.
              </p>
              <p>
                The monocrystalline solar cell is encapsulated by a
                front tempered glass layer.
              </p>
            </div>
            <div class="solar-tech__features">
              <img
                class="solar-tech__icon"
                alt="light icon" 
                src="https://camas.app/resources/static/images/images/image__ef679db882ac458ab620017a752ce48e.png">
              <div>
                Engineered to allow light to penetrate and reach the photovoltaic layer.
              </div>
            </div>
            <div class="solar-tech__features">
              <img 
                class="solar-tech__icon"
                alt="color icon" 
                src="https://camas.app/resources/static/images/images/image__70ee07aa48594d70bd2e9981e12690d1.png">
              <div>
                Colour treated to maintain design flexibility.
              </div>
            </div>
            <div class="solar-tech__features">
              <img
                class="solar-tech__icon"
                alt="performance icon" 
                src="https://camas.app/resources/static/images/images/image__8ba6bb94a276cb1fbba71b833719fc09.png">
              <div>
                Anti-reflective and anti-soiling coating with equiped solar
                back sheet that enhance efficiency.
              </div>
            </div>
            <!--<div class="solar-tech__features">
              <img
                class="solar-tech__icon"
                alt="protection icon" 
                src="https://camas.app/resources/static/images/images/image__96a0a570aa0d46092d68b826860706c5.png">
              <div>
                Protects the module from UV radiation, chemical corrosion,
                and oxygen/moisture penetration while providing electrical
                insulation.
              </div>
            </div>-->
          </div>
          <div class="bottom-panel">
            <div class="solar-tech__tech-label">
              Attachment System
            </div>
            <div>Interlocking Channel System</div>
            <img
              class="interlocking-channel"
              alt="interlocking channel system" 
              src="https://camas.app/resources/static/images/images/image__f4d08ab18aaecca2ba96f2aaf159603a.jpg"/>
          </div>
        </div>
        <div class="solar-cladding__right">
          <div>Complete Design Flexibility</div>
          <div class="section__title">Solar Cladding</div>
          <div class="section__desc">
            Mitrex offers architects the control and flexibility to design structures
            sustainably without compromising aesthetics.
          </div>
          <img
            class="cladding-samples"
            alt="cladding samples" 
            src="https://camas.app/resources/static/images/images/image__5a128af2f4d2c90735331a46223be8f1.png"/>
          <img 
            class="more-options"
            alt="more options"
            src="https://camas.app/resources/static/images/images/image__d32f01944cf1917ae51e5c1c141a6978.png"/>
          <div class="cladding-benefits__wrapper">
            <div 
              class="cladding-benefits__item"
              v-for="(benefit, i) in solarCladdingBenefits"
              :key="i">
              <img 
                class="cladding-benefits__icon" 
                :src="benefit.icon"/>
              <div class="cladding-benefits__label">
                {{ benefit.text }}
              </div>
            </div>
          </div>
          <div class="section__title">Adaptable Panel Sizes</div>
          <div class="section__desc">
            Mitrex offers Solar Cladding panels in virtually any size. Our standard panel
            sizes range from 2-3.5 ft by 4-6.66 ft. When larger panels are needed, we also
            offer customized panels that can be a maximum of 6.5 ft by 9.5 ft.
          </div>
          <img
            class="panel-sizes"
            alt="adaptable panel sizes" 
            src="https://camas.app/resources/static/images/images/image__4e2114a7d32ec54d02167fa4a987d311.png"/>
          <!--<div class="section__title">Technical Details</div>
          <div class="tech-details__row-wrapper">
            <div 
              class="tech-details__detail"
              v-for="detail in solarCladdingTechnicalDetails"
              :key="detail.dimension">
              <img
                class="tech-details__bullet"
                alt="lightning bullet point" 
                src="https://camas.app/resources/static/images/images/image__7db9e89ce198e4b592eb62ccfb9b3bc7.png"/>
              <div>
                <div class="tech-details__dimension">{{ detail.dimension }}</div>
                <div>{{ detail.measure }}</div>
              </div>
            </div>
          </div>-->
        </div>
      </section>

      <section class="solar-tech solar-glass break-after">
        <div class="solar-glass__left">
          <div class="solar-tech__title-wrapper">
            <img 
              class="solar-tech__icon"
              src="https://camas.app/resources/static/images/images/image__2822378e663bc5dbfb21bfdd8cd1834a.png"
              alt="solar technology icon"/>
            <div>Solar Technology</div>
          </div>
          <p>
            Mitrex Solar Glass uses
            photovoltaic technology
            integrated into glass
            products to produce clean,
            sustainable energy.
          </p>
          <div class="solar-tech__features">
            <img 
              class="solar-tech__icon"
              src="https://camas.app/resources/static/images/images/image__c166047e7a5154838760b4197edc5fcc.png"
              alt="solar cell icon"/>
            <div>Relies on advanced solar technology.</div>
          </div>
          <div class="solar-tech__features">
            <img 
              class="solar-tech__icon"
              src="https://camas.app/resources/static/images/images/image__22718f52ef7e731dc9339d6f0daedfa4.png"
              alt="guarantee icon"/>
            <div>
              Available in Insulated Glass Units (IGU) for
              secure and durable insulation.
            </div>
          </div>
          <div class="solar-tech__features">
            <img 
              class="solar-tech__icon"
              src="https://camas.app/resources/static/images/images/image__8ba6bb94a276cb1fbba71b833719fc09.png"
              alt="performance icon"/>
            <div>
              Comprised of two layers of heat-tempered glass
              for superior thermal performance.
            </div>
          </div>
          <div class="solar-tech__title-wrapper">
            <img 
              class="solar-tech__icon"
              src="https://camas.app/resources/static/images/images/image__c166047e7a5154838760b4197edc5fcc.png"
              alt="solar cell icon"/>
            <div>Solar Cells</div>
          </div>
          <p>
            Mitrex builds upon advanced solar technology by
            combining it with building materials, making solar
            integrated, multi-purpose products.
          </p>
          <div class="solar-tech__tech-label">Transparent Solar Technology</div>
          <img 
            class="solar-tech__image"
            src="https://camas.app/resources/static/images/images/image__156ce0f6628c7228a8d2200454ee9b8d.png"/>
          <p>
            Transparent solar technology
            is for seamless integration of
            solar without the design
            drawbacks.
          </p>
          <div class="solar-tech__tech-label">Monocrystalline Solar Cell</div>
          <img 
            class="solar-tech__image--square"
            src="https://camas.app/resources/static/images/images/image__443c621c20b54bbb2561a1e5976739ac.png"/>
          <p>
            This type of solar technology
            is the highest performing,
            most scalable option on the
            market.
          </p>
        </div>

        <div class="solar-glass__right">
          <div>Aesthetically-Pleasing</div>
          <div class="section__title">Solar Glass</div>
          <div class="section__desc">
            Mitrex Solar Glass was created with design in mind - our vision ensures that
            architects never have to make the choice between sustainability and
            aesthetics.
          </div>
          <div class="glass-options__wrapper">
            <div 
              class="glass-options__item"
              v-for="option in solarGlassOptions"
              :key="option.name">
              <div class="glass-options__images">
                <img class="glass-options__image" :src="option.mainImage"/>
                <img class="glass-options__battery" :src="option.batteryIcon"/>
              </div>
              <div class="glass-options__text">
                <span class="glass-options__name">
                  {{ option.name }} - 
                </span>
                <span class="glass-options__detail">
                  {{ option.detail }}
                </span>
              </div>
            </div>
          </div>
          <div class="tech-details__wrapper right">
            <!--<div class="tech-details__text">
              <div 
                class="tech-details__detail"
                v-for="detail in solarGlassTechnicalDetails"
                :key="detail.dimension">
                <img
                  class="tech-details__bullet" 
                  src="https://camas.app/resources/static/images/images/image__7db9e89ce198e4b592eb62ccfb9b3bc7.png"/>
                <div>
                  <div class="tech-details__dimension">{{ detail.dimension }}</div>
                  <div>{{ detail.measure }}</div>
                </div>
              </div>
            </div>-->
            <div class="tech-details__sizes-label">
              Available in a variety of sizes
            </div>
            <img 
              class="tech-details__glass-section"
              src="https://camas.app/resources/static/images/images/image__a9adfb702a7ec4813e6609ca3bb59d03.png"
              alt="solar glass section">
            <div class="tech-details__igu-label">
              Insulated Glass Unit (IGU)
            </div>
          </div>
          <div class="glass-benefits__wrapper">
            <div
              class="glass-benefits__item"
              v-for="benefit in solarGlassBenefits"
              :key="benefit.text">
              <img class="glass-benefits__icon" :src="benefit.icon" :alt="benefit.text"/>
              <div class="glass-benefits__label">{{ benefit.text }}</div>
            </div>
          </div>
        </div>
      </section>

      <section class="break-after">
        <div class="services__text">
          <div>
            Mitrex
          </div>
          <div class="section__title">
            Turnkey Services
          </div>
        </div>
        <div class="services__wrapper">
          <div
            class="services__item"
            v-for="service in services"
            :key="service.name">
            <img 
              class="services__icon"
              :alt="service.name"
              :src="service.link"/>
            <div class="services__label">
              {{ service.name }}
            </div>
          </div>
        </div>
        <div class="purchase-options">
          <div class="section__title">
            Purchase Options
          </div>
          <div class="purchase-options__wrapper"> 
            <div class="purchase-options__section">
              <div class="purchase-options__option-header">
                Mitrex Power Agreement (MPA)
              </div>
              <div class="purchase-options__option-desc">
                <p>
                  The Mitrex Power Agreement (MPA)
                  is the first pricing model offered to
                  building owners. When buying Mitrex
                  solar products through the Mitrex Power
                  Agreement (MPA), <strong>Mitrex only charges
                  the cost of the material (cladding,
                  glass, etc.) to lower the upfront fees
                  significantly.</strong> Through the MPA, Mitrex
                  sells back the electricity generated
                  through Mitrex solar integrated products
                  to the structure.
                </p>
              </div>
              <ul class="purchase-options__details purchase-options__details--push-right">
                <li 
                  class="purchase-options__detail"
                  v-for="(detail, i) in mpaDetails"
                  :key="i">
                  <div class="purchase-options__detail__text" v-html="detail.text">
                  </div>
                  <div class="purchase-options__detail__label">
                    <img v-if="detail.image" :src="detail.image"/>
                    <div v-if="detail.label">{{ detail.label }}</div>
                  </div>
                </li>
              </ul>
            </div>
            <div class="purchase-options__section">
              <div class="purchase-options__option-header">
                Direct Purchase Agreement (DPA)
              </div>
              <div class="purchase-options__option-desc">
                <p>
                  The Direct Purchase Agreement (DPA)
                  is the second pricing model offered to
                  building owners. When buying Mitrex
                  solar products through the Direct
                  Purchase Agreement (DPA), <strong>building
                  owners will fully own the products and
                  all the electricity they generate.</strong> Mitrex
                  profits from the material sold, not the
                  energy generated. Building owners will
                  benefit from a high rate of return and a
                  short payback period for their investment.
                </p>
              </div>
              <ul class="purchase-options__details purchase-options__details--push-left">
                <li 
                  class="purchase-options__detail"
                  v-for="(detail, i) in dpaDetails"
                  :key="i">
                  <div class="purchase-options__detail__label">
                    <img v-if="detail.image" :src="detail.image"/>
                    <div v-if="detail.label">{{ detail.label }}</div>
                  </div>
                  <div class="purchase-options__detail__text" v-html="detail.text">
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section>
        <div class="applications__text">
          <div>
            Any Application
          </div>
          <div class="section__title">
            Mitrex Solar Products
          </div>
          <div class="section__desc">
            From the shingles atop a residential roof to the cladding and balcony railings
            of a high-rise tower, or even the glass enveloping a greenhouse, Mitrex Solar
            Technology offers invisible power generation for virtually any application.
          </div>
        </div>
        <div class="applications__wrapper">
          <div
            class="applications__tile"
            v-for="application in applications"
            :key="application.name">
            <img 
              :alt="application.name"
              :src="application.link"/>
          </div>
        </div>
      </section>

      <section data-type="last-page">
        <div class="last-page__bg">
          <img 
            src="https://camas.app/resources/static/images/images/image__1c1d9a37113ea9f56ac8ad071994a559.png"
            alt="cover background image"/>
        </div>
        <div class="last-page__bg-overlay__wrapper">
          <div class="last-page__bg-overlay__inner">
            <img 
              class="last-page__logo"
              src="https://camas.app/resources/static/images/images/image__40f94364c998232c96ff71ee3fb00978.png"
              alt="Mitrex Logo"/>
            <div class="last-page__contact-us">
              CONTACT US
            </div>
            <div class="last-page__contact-info">
              <div class="contact-info__item">
                <img
                  class="contact-info__icon" 
                  src="https://camas.app/resources/static/images/images/image__24f0455ad0662fff46d21a7b13674f59.png"
                  alt="phone icon"/>
                <div 
                  class="contact-info__detail">
                  +1-416-497-7120
                </div>
              </div>
              <div class="contact-info__item">
                <img
                  class="contact-info__icon" 
                  src="https://camas.app/resources/static/images/images/image__45cfdb856e64e025f18ff05ce3cf3f36.png"
                  alt="email icon"/>
                <a 
                  href="mailto:info@mitrex.com"
                  class="contact-info__detail">
                  info@mitrex.com
                </a>
              </div>
              <div class="contact-info__item">
                <img
                  class="contact-info__icon" 
                  src="https://camas.app/resources/static/images/images/image__a2a0645456c08a74e0cdb1292d1ab5cf.png"
                  alt="website icon"/>
                <a 
                  href="www.mitrex.com"
                  target="_blank"
                  class="contact-info__detail">
                  mitrex.com
                </a>
              </div>
              <div class="contact-info__item">
                <img
                  class="contact-info__icon" 
                  src="https://camas.app/resources/static/images/images/image__b03f9e98af487c26df6b0c9b0f54dc44.png"
                  alt="address icon"/>
                <div 
                  class="contact-info__detail">
                  41 Racine Rd., Toronto, ON M9W 2Z4, Canada
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>

    <div id="preview-output">
    </div>
  </div>
</template>

<script>
import { Previewer } from 'pagedjs'
import mitrexReportStyle from '@/helpers/mitrexReportStyle'

export default {
  props: {
    sunlightHours: {
      type: Object
    },
    projectSize: {
      type: Object
    },
    projectBuilding: {
      type: Object
    },
    projectSummary: {
      type: Object
    },
    projectCladding: {
      type: Object
    },
    projectRailing: {
      type: Object
    },
    projectWindow: {
      type: Object
    },
    taxIncentives: {
      type: Array
    }
  },
  data() {
    return {
      solarCladdingBenefits: [
        { text: "Customizable pattern, texture and colour options to satisfy any architectural needs.", icon: "https://camas.app/resources/static/images/images/image__1e0565ee87d88bfb78bd49b05dbf738a.png" },
        { text: "Reflective, semi-reflective or matte module depending on aesthetic requirements.", icon: "https://camas.app/resources/static/images/images/image__f180b93038d9c63567fea5d346ba8240.png" },
        { text: "Design flexibility, including size of panels, curves and corners.", icon: "https://camas.app/resources/static/images/images/image__f0ea5ab624c5a5588957524f76d446e7.png" },
      ],
      solarCladdingTechnicalDetails: [
        { dimension: "Max Power", measure: "100W to 420W" },
        { dimension: "Color Efficiency Drop", measure: "0% TO 40%" },
        { dimension: "Voc", measure: "11.8V to 47.2V" },
        { dimension: "Isc", measure: "5.86A to 10.15A" }
      ],
      solarGlassOptions: [
        { name: "Transparent", detail: "Transparent Technology", mainImage: "https://camas.app/resources/static/images/images/image__4baaac4b6df9c4efaa214f0ac34e96b1.png", batteryIcon: "https://camas.app/resources/static/images/images/image__4f48b883d55c42aa9ec9b13e5ab0d681.png" },
        { name: "Semi-Transparent", detail: "Monocrystalline Solar Cell", mainImage: "https://camas.app/resources/static/images/images/image__a1f3d64daca848ba542e05ab309f9072.png", batteryIcon: "https://camas.app/resources/static/images/images/image__ca57430589c08e7f7f8f01a1c6dcad02.png" },
        { name: "Transitional", detail: "Monocrystalline Solar Cell", mainImage: "https://camas.app/resources/static/images/images/image__393ffe11dbb6f40b2b6f2630d0862e34.png", batteryIcon: "https://camas.app/resources/static/images/images/image__4f48b883d55c42aa9ec9b13e5ab0d681.png" },
        { name: "Semi-Opaque", detail: "Monocrystalline Solar Cell", mainImage: "https://camas.app/resources/static/images/images/image__f3207239a4a76bd348c0b495e351dcf6.png", batteryIcon: "https://camas.app/resources/static/images/images/image__3480e0f067d9c18b5d1995e54fca5032.png" }
      ],
      solarGlassBenefits: [
        { text: "Green Energy Generation", icon: "https://camas.app/resources/static/images/images/image__ffc989b656627f0937daba7704ff3413.png" },
        { text: "Transparency Options", icon: "https://camas.app/resources/static/images/images/image__d463088ae934ed1c3f42ba75ac98d310.png" },
        // { text: "Customizable Tints & Colors", icon: "https://camas.app/resources/static/images/images/image__958448c0f98eba4cd470f7e597960a35.png" },
        { text: "Weather Resistant", icon: "https://camas.app/resources/static/images/images/image__eb65a94b3162a0c6afd178dd572397cb.png" },
        { text: "Invisible Electrical Connections", icon: "https://camas.app/resources/static/images/images/image__4a71f2477ee1b469208974e6e77832c1.png" },
        // { text: "Textured Glass Varieties", icon: "https://camas.app/resources/static/images/images/image__7641e4460dbc4e87def25b2f9fa9cc4d.png" },
        { text: "High-tech Manufacturing", icon: "https://camas.app/resources/static/images/images/image__3a5b8984887d8cb9053d2ab120f59791.png" },
        { text: "Turnkey Services", icon: "https://camas.app/resources/static/images/images/image__de11ff22c1553f5afb3115d294aee4d9.png" }
      ],
      solarGlassTechnicalDetails: [
        { dimension: "Glass Dimensions", measure: "1900mm x 1100mm" },
        { dimension: "Max Power", measure: "320W" },
        { dimension: "Voc", measure: "39.2W" },
        { dimension: "Isc", measure: "9.82A" }
      ],
      services: [
        { name: "Analysis Stage", link: "https://camas.app/resources/static/images/images/image__82fc45d863ffebe902e4eacd04255a23.png"},
        { name: "Planning Stage", link: "https://camas.app/resources/static/images/images/image__fe4d5c9169d502e2e58aaabe4146a92f.png" },
        { name: "Budget Assessment", link: "https://camas.app/resources/static/images/images/image__6d9f9cc5d277eccb92b381a95c4cb9e0.png" },
        { name: "Applications Solutions", link: "https://camas.app/resources/static/images/images/image__290ea6ccd09c33b986255b83f6a676eb.png"},
        { name: "Project Management", link: "https://camas.app/resources/static/images/images/image__22652b8ea4f4140ae09809d3ffc3d4d0.png" }
      ],
      mpaDetails: [
        { 
          text: "Receive <strong>fully integrated photovoltaic system.</strong>", 
          image: "https://camas.app/resources/static/images/images/image__6b02e166bed01ccfa091a88e8d7ee8ec.png" 
        },
        { 
          text: "<strong>Enter a 30-year energy contract with Mitrex</strong>; Energy generated by the system is owned by Mitrex.", 
          label: "ENERGY" 
        },
        { 
          text: "<strong>Efficient building</strong> at significantly below-market prices.", 
          image: "https://camas.app/resources/static/images/images/image__e1873d28987ecc3d3e3b05c3aa9fc6e0.png" 
        },
        { 
          text: "<strong>Energy generated</strong> by the system <strong>is sold</strong> to building <strong>at a lower rate</strong> than electricity from the grid.", 
          label: "OUTCOME" 
        }
      ],
      dpaDetails: [
        { 
          text: "Receive <strong>fully integrated photovoltaic system.</strong>", 
          label: "PRODUCT" 
        },
        { 
          text: "<strong>Full ownership</strong> of energy generated by the system.", 
          image: "https://camas.app/resources/static/images/images/image__28519e918f4780516f0734ab67555cad.png" 
        },
        { 
          text: "<strong>High rate of return</strong> on investment in BIPV.", 
          label: "WARRANTY" 
        },
        { 
          text: "<strong>Full ownership of energy</strong> generated by the system.", 
          image: "https://camas.app/resources/static/images/images/image__6d9f9cc5d277eccb92b381a95c4cb9e0.png" 
        }
      ],
      applications: [
        { name: "Solar Cladding", link: "https://camas.app/resources/static/images/images/image__88252b7a7781f6783322a054f332b431.png" },
        { name: "Solar Windows", link: "https://camas.app/resources/static/images/images/image__cdfaecb80a4457517fb64292e03297fd.png" },
        { name: "Solar Roof", link: "https://camas.app/resources/static/images/images/image__7c1a1d1f5195d3ae6b54416dfdfa5b18.png" },
        { name: "Solar Panels", link: "https://camas.app/resources/static/images/images/image__434e851347db4dc9847c78661a9f329a.png" },
        { name: "Solar Glass", link: "https://camas.app/resources/static/images/images/image__014f91c73a3f6f858ea051ae2ac107b9.png" },
        { name: "Solar Siding", link: "https://camas.app/resources/static/images/images/image__b8d63d7f90c2c46d33489fa2b32faa32.png" },
        { name: "Solar Railing", link: "https://camas.app/resources/static/images/images/image__f9ce0143595837d74e2892ececbd7b39.png" },
        { name: "Solar Greenhouse", link: "https://camas.app/resources/static/images/images/image__e65f1620cc48507aabf6bdd4013300bf.png" },
        { name: "Military Solar", link: "https://camas.app/resources/static/images/images/image__b87cb55e80e8f10138bb9beb5b502cdc.png" },
      ],
      windowWidth: 1519
    }
  },
  // computed: {
  //   previewStyle({ windowWidth }) {
  //     let width = 1056;
  //     let scale = windowWidth < width ? windowWidth / width : 1;
  //     return {
  //       transform: `scale(${scale})`,
  //       transformOrigin: 'top'
  //     }
  //   }
  // },
  mounted() {
    this.preview();
    this.$nextTick(() => {
      window.addEventListener('resize', this.onResize);
    })
  },
  beforeDestroy() { 
    window.removeEventListener('resize', this.onResize); 
  },
  methods: {
    preview() {
      const previewer = new Previewer();

      var fragment = new DocumentFragment();
      let previewEls = [].slice.call(document.querySelector("#preview").childNodes);

      previewEls.forEach(child => {
        fragment.appendChild(child);
      })

      previewer
        .preview(
          fragment,
          [{ "inline": mitrexReportStyle }],
          document.querySelector("#preview-output")
        )
        .then(flow => {
          this.$emit('rendered', true);
          console.log("preview rendered, total pages", flow.total, { flow });
        });
    },
    onResize() {
      this.windowWidth = window.innerWidth;
    }
  }
}
</script>

<style scoped lang="scss">
@import '../../styles/interface.css';

#preview {
  display: none;
}

#preview-output {
  font-family: 'Montserrat', sans-serif;
  font-size: 0.95em;
  color: #0E4E74;
  strong {
    color: #0E4E74;
  }
}

.section {
  &__title {
    font-weight: bold;
    margin-bottom: 10px;
    font-size: 22px;
    text-align: center;
  }
  &__desc {
    width: 80%;
    font-size: 0.9em;
  }
  &__subsection {
    margin: 10px 0px;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
}

.cover {
  &__content {
    top: 0px;
    left: 0px;
    position: absolute;
    width: 100%;
    height: 100%;
    z-index: 0;
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    padding: 10mm;
  }
  &__bg-container {
    width: 100%;
    height: 100%;
    overflow: hidden;
  }
  &__banner {
    height: 250px;
    bottom: 105px;
    width: 100%;
    position: absolute;
    z-index: 1;
    background: rgba(14,78,116,0.75);
  }
  &__banner-inner {
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
  }
  &__logo {
    width: 500px;
    height: auto;
  }
  &__summary-font {
    z-index: 2;
    font-size: 28px;
    font-weight: bold;
    color: #fff;
    margin-top: 12px;
  }
  &__banner-tagline {
    position: absolute;
    bottom: 0px;
    transform:translateY(50%);
    color: #fff;
    background: #F16635;
    font-weight: bold;
    font-size: 20px;
    padding: 10px 55px;
    border-radius: 200px;
  }
}

.mission {
  page-break-after: always;
  &__bg-container {
    position: absolute;
    top: 0px;
    left: 0px;
    width: 100%;
    height: 100%;
    overflow: hidden;
  }
  &__wrapper {
    position: absolute;
    margin: 35px 30px;
    background: rgba(241, 102, 33, 0.95);
    color: #fff;
    padding: 30px;
    width: 51%;
    z-index: 1;
  }
  &__title {
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-bottom: 12px;
    .target-icon {
      width: 60px;
      height: auto;
    }
    .title-line {
      margin-left: 10px;
      font-weight: bold;
      font-size: 30px;
    }
  }
}

.project-details {
  .running-footer {
    display: none !important;
  }
}

.running-footer {
  display: flex !important;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: center;
  width: calc(var(--pagedjs-width) - var(--pagedjs-margin-left) - var(--pagedjs-margin-right));
  margin-top: -5px;
  &__left {
    background: #0E4E74;
    height: 5px;
    width: 450px;
    position: relative;
    &:after {
      content: '';
      position: absolute;
      top: 0px;
      right: -3px;
      width: 3px;
      height: 105%;
      background: #fff;
      -webkit-transform-origin: 100% 0;
      -ms-transform-origin: 100% 0;
      transform-origin: 100% 0;
      -webkit-transform: skew(-45deg);
      -ms-transform: skew(-45deg);
      transform: skew(-45deg);
      z-index: 1;
    }
  }
  &__right {
    background:#F16635;
    height: 5px;
    width: calc(var(--pagedjs-width) - 450px - var(--pagedjs-margin-left) - var(--pagedjs-margin-right));
    position: relative;
    &:after {
      content: '';
      position: absolute;
      top: 0px;
      left: 3px;
      width: 3px;
      height: 105%;
      background: #fff;
      -webkit-transform-origin: 100% 0;
      -ms-transform-origin: 100% 0;
      transform-origin: 100% 0;
      -webkit-transform: skew(-45deg);
      -ms-transform: skew(-45deg);
      transform: skew(-45deg);
      z-index: 1;
    }
  }
  .contact-info {
    position: relative;
    padding-top: 5px;
    padding-bottom: 10px;
    display: flex;
    flex-direction: row;
    font-size: 10px;
    justify-content: center;
    &__item {
      display: flex;
      flex-direction: row;
      align-items: center;
      margin: 0px 10px;
    }
    &__icon {
      width: 12px;
      margin-right: 5px;
      height: auto;
    }
  }
}

.project-details {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;

  &__left {
    width: 29%;
    flex-shrink: 0;
    font-size: 12px;
    .orange-panel {
      padding: 15px;
      background: #F16635;
      color: #fff;
      display: flex;
      flex-direction: column;
      align-items: center;

      .building-image {
        width: 150%;
        margin: 10px 0px;
        height: auto;
        flex-shrink: 0;
        flex-grow: 1;
      }

      .summary__section {
        &:not(:first-child) {
          margin-top: 15px;
        }
      }
    }
    .bottom-panel {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 10px 0px;
      text-align: center;
      .product {
        margin-top: 5px;
        &__image {
          width: 140px;
          &--cladding {
            width: 120px;
            margin: 10px 10px;
          }
        }
      }
    }
    .panel {
      &__title {
        font-size: 16px;
        font-weight: bold;
        text-align: center;
      }
      &__subtitle {
        font-weight: bold;
        font-size: 12px;
      }
    }
  }
  &__right {
    width: 72%;
    flex-shrink: 0;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    table, th {
      color: #0E4E74;
      font-size: 12.5px;
    }
    table {
      width: 90%;
      td:nth-child(2), th:nth-child(2) {
        background: #E6ECF2;
      }
    }
    td {
      vertical-align: middle;
    }
    .env-impact {
      padding-left: 30px;
      &__graphics {
        margin: 10px 0px;
        font-size: 12.5px;
      }
      &__wrapper {
        display: flex;
        flex-direction: row;
        &.top {
          padding-left: 168px;
        }
        &.bottom {
          margin-left: -18px;
        }
      }
      &__item {
        width: 150px;
        &.extra-margin {
          margin-right: 110px;
        }
      }
      &__number {
        font-weight: bold;
        color: #F16635;
      }
      &__footnote {
        font-size: 11px;
      }
    }
    .tax-incentives {
      font-size: 14px;
    }
  }
}

.solar-tech {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;

  &__title-wrapper {
    display: flex;
    flex-direction: row;
    font-size: 16px;
    font-weight: bold;
    text-align: center;
    align-items: center;
    align-self: start;
    &:not(:first-child) {
      margin-top: 10px;
    }
  }
  &__icon {
    width: 30px;
    margin-right: 5px;
  }
  &__features {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    padding: 5px 0px;
  }
  &__tech-label {
    font-weight: bold;
    text-align: center;
    font-size: 12px;
    margin: 5px 0px;
  }
  &__image {
    height: 85px;
  }
  &__image--square {
    height: 110px;
  }

  .tech-details {
    margin-top: 10px;
    width: 100%;
    &__wrapper {
      display: flex;
      flex-direction: row;
      align-items: center;
      margin-top: 15px;
      justify-content: center;
      position: relative;
      width: 100%;
      &.right {
        margin-top: 0px;
        justify-content: flex-end;
      }
    }
    &__row-wrapper {
      display: flex;
      flex-direction: row;
      align-items: center;
    }
    &__text {
      padding: 10px;
    }
    &__detail {
      display: flex;
      flex-direction: row;
      align-items: center;
      text-align: left;
      font-size: 12px;
      padding: 5px 10px;
    }
    &__bullet {
      width: 8px;
      margin-right: 5px;
    }
    &__dimension {
      font-weight: bold;
    }
    &__glass-section {
      width: 320px;
    }
    &__sizes-label {
      position: absolute;
      left: 30px;
      bottom: 20px;
      color: #fff;
      background: #0E4E74;
      padding: 5px 10px;
    }
    &__igu-label {
      position: absolute;
      right: 10px;
      bottom: 25px;
      color: #fff;
      background: #F16635;
      font-size: 10px;
      font-weight: bold;
      border-radius: 200px;
      padding: 3px 10px;
    }
  }
}

.solar-cladding {
  &__left {
    width: 29%;
    flex-shrink: 0;
    font-size: 12.5px;
    .orange-panel {
      padding: 15px;
      background: #F16635;
      color: #fff;
      display: flex;
      flex-direction: column;
      align-items: center;
      p:not(:last-child) {
        margin-top: 10px;
      }
      p:last-child {
        margin: 10px 0px;
      }
    }
    .bottom-panel {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 10px 0px;
      .interlocking-channel {
        max-width: 180% !important;
        flex-shrink: 0;
        width: 350px;
        margin-left: -20px;
        margin-top: 5px;
        height: auto;
      }
    }
  }
  &__right {
    width: 72%;
    flex-shrink: 0;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    .cladding-samples {
      padding: 20px;
    }
    .more-options {
      width: 180px;
      height: auto;
    }
    .cladding-benefits {
      &__wrapper {
        display: flex;
        flex-direction: row;
        align-items: flex-start;
        justify-content: center;
        margin: 15px 20px;
      }
      &__item {
        max-width: 200px;
        margin: 5px;
      }
      &__icon {
        height: 45px;
        width: auto;
      }
      &__label {
        font-size: 11px;
        font-weight: bold;
      }
    }
    .panel-sizes {
      width: 88%;
      height: auto;
      margin-top: 5px;
      margin-bottom: 15px;
    }
  }
}

.solar-glass {
  &__left {
    width: 29%;
    flex-shrink: 0;
    height: 100%;
    padding: 15px;
    background: #F16635;
    color: #fff;
    font-size: 12.5px;
    display: flex;
    flex-direction: column;
    align-items: center;
    p:not(:last-child) {
      margin: 7px 0px;
    }
    p:last-child {
      margin-top: 7px;
    }
  }
  &__right {
    width: 73%;
    flex-shrink: 0;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    .glass-options {
      &__wrapper {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        margin-top: 20px;
        margin-bottom: 25px;
      }
      &__item {
        width: 50%;
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 10px 5px;
      }
      &__images {
        display: flex;
        flex-direction: row;
        align-items: flex-end;
      }
      &__image {
        height: 110px;
        width: auto;
      }
      &__text {
        margin-top: 5px;
      }
      &__battery {
        width: 15px;
        height: auto;
      }
      &__name {
        font-weight: bold;
        font-size: 12.5px;
      }
      &__detail {
        font-size: 10px;
      }
    }
    .glass-benefits {
      &__wrapper {
        width: 80%;
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        justify-content: center;
        margin-top: 35px;
      }
      &__item {
        max-width: 120px;
        margin: 5px;
      }
      &__icon {
        height: 30px;
        width: auto;
      }
      &__label {
        font-size: 12.5px;
        font-weight: 500;
      }
    }
  }
}

.services {
  &__text {
    text-align: center;
    padding: 15px;
  }
  &__wrapper {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-around;
  }
  &__item {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: 0px 10px;
    max-width: 105px;
    text-align: center;
  }
  &__icon {
    width: auto;
    height: 65px;
    align-self: center;
  }
  &__label {
    font-weight: bold;
    font-size: 12px;
    margin-top: 5px;
  }
}

.purchase-options {
  margin-top: 30px;
  &__wrapper {
    display: flex;
    flex-direction: row;
  }
  &__section {
    width: 50%;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 10px 25px;
    &:first-child {
      border-right: 1px dotted #0E4E74;
    }
    p {
      margin: 5px 0px;
    }
  }
  &__option-desc {
    height: 280px;
  }
  &__option-header {
    width: 65%;
    font-size: 16px;
    background: #F16635;
    padding: 8px 15px;
    border-radius: 200px;
    font-weight: bold;
    color: white;
    text-align: center;
    margin-bottom: 15px;
  }
  &__details {
    list-style-type: none;
    display: flex;
    flex-direction: column;
    &--push-right {
      align-items: flex-end;
      .purchase-options__detail__label {
        transform: translateX(15%);
        text-align: right;
        div::before {
          content: "◀";
          position: absolute;
          right: -13px;
          font-size: 14px;
          line-height: 1;
          color: #0E4E74;
        }
      }
    }
    &--push-left {
      align-items: flex-start;
      .purchase-options__detail__text {
        text-align: right;
      }
      .purchase-options__detail__label {
        transform: translateX(-15%);

        div::before {
          content: "▶";
          position: absolute;
          left: -14px;
          font-size: 14px;
          height: 100%;
          line-height: 1;
          color: #0E4E74;
        }
      }
    }
  }
  &__detail {
    display: flex;
    flex-direction: row;
    align-items: center;
    width: 95%;
    height: 75px;
    padding: 10px 0px;
    font-size: 12.5px;
    img {
      height: auto;
      width: 40px;
      align-self: center;
    }
    &__label {
      min-width: 75px;
      font-weight: bold;
      position: relative;
      color: #F16635;
      padding: 0px 5px;
    }
    &:not(:last-child) {
      border-bottom: 1px solid #0E4E74;
    }
  }
}

.applications {
  &__text {
    width: 100%;
    padding: 15px;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  &__wrapper {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    margin: 0px -10px;
  }
  &__tile {
    padding: 10px;
    width: calc(100% / 3);
    img {
      display: block;
    }
  }
}

.last-page {
  &__bg {
    height: 100%;
    width: 100%;
    z-index: 1;
  }
  &__bg-overlay {
    &__wrapper {
      position: absolute;
      left: 0px;
      top: 0px;
      width: 100%;
      height: 100%;
      z-index: 2;
      padding: 10mm;
    }
    &__inner {
      background: rgba(14,78,116,0.75);
      width: 100%;
      height: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
    }
  }
  &__logo {
    width: 500px;
    height: auto;
  }
  &__contact-us {
    font-weight: bold;
    font-size: 30px;
    margin: 15px 0px;
    color: #fff;
  }
  &__contact-info {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
    width: 100%;
    .contact-info {
      &__item {
        display: flex;
        flex-direction: row;
        align-content: center;
        margin: 0px 5px 8px 8px;
      }
      &__icon {
        width: 28px;
        height: auto;
        margin-right: 5px;
        align-self: center;
      }
      &__detail {
        color: #fff;
        font-weight: 900;
        font-size: 19px;
      }
    } 
  }
}
</style>